import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Tag, Button, Empty, message, Modal } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { QRCode } from 'antd';
import { orderAPI, paymentsAPI } from '../services/api';
import { requireAuth } from '../utils/auth';
import './Orders.css';

function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [qrVisible, setQrVisible] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  useEffect(() => {
    if (!requireAuth(navigate)) return;
    loadOrders();
    return () => {
      // 组件卸载时清除轮询
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pagination.current]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await orderAPI.getOrders({
        page: pagination.current,
        limit: pagination.pageSize
      });
      setOrders(res.data.orders);
      setPagination(prev => ({
        ...prev,
        total: res.data.pagination.total
      }));
    } catch (error) {
      message.error('加载订单失败');
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'orange', text: '待支付' },
      paid: { color: 'blue', text: '已支付' },
      shipped: { color: 'cyan', text: '已发货' },
      completed: { color: 'green', text: '已完成' },
      cancelled: { color: 'red', text: '已取消' }
    };
    return statusMap[status] || statusMap.pending;
  };

  const handlePayment = async (order) => {
    setCurrentOrder(order);
    setPaymentLoading(true);
    setQrVisible(true);
    try {
      console.log('[订单支付] 开始创建支付，订单ID:', order.id);
      const res = await paymentsAPI.createOrderPayment({ order_id: order.id });
      console.log('[订单支付] 支付创建成功:', res);
      setPaymentUrl(res.data.payment_url);

      // 开启轮询检测支付状态
      startPolling(res.data.order_no);
    } catch (error) {
      console.error('[订单支付] 支付创建失败:', error);
      message.error(error.message || '获取支付链接失败');
      setPaymentUrl('');
      setQrVisible(false);
    } finally {
      setPaymentLoading(false);
    }
  };

  const startPolling = (orderNo) => {
    // 清除旧的轮询
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    console.log('[订单轮询] 开始轮询订单:', orderNo);
    let pollCount = 0;
    const maxPolls = 100; // 最多轮询100次（5分钟）

    const interval = setInterval(async () => {
      pollCount++;
      console.log(`[订单轮询] 第${pollCount}次轮询，订单号:`, orderNo);

      try {
        const res = await paymentsAPI.getOrderPaymentStatus(orderNo);
        console.log(`[订单轮询] 第${pollCount}次轮询结果:`, res);

        if (res.data.is_paid) {
          // 支付成功
          console.log('[订单轮询] 检测到支付成功！');
          clearInterval(interval);
          setPollingInterval(null);
          message.success('支付成功！');
          setQrVisible(false);

          // 重新加载订单列表
          loadOrders();
        } else {
          console.log(`[订单轮询] 第${pollCount}次检测，订单状态: ${res.data.status}，未支付`);
        }
      } catch (error) {
        console.error('[订单轮询] 轮询订单状态失败:', error);
      }

      // 达到最大轮询次数，停止轮询
      if (pollCount >= maxPolls) {
        console.log('[订单轮询] 达到最大轮询次数，停止轮询');
        clearInterval(interval);
        setPollingInterval(null);
        message.info('支付超时，请刷新页面查看');
      }
    }, 3000); // 每3秒轮询一次

    setPollingInterval(interval);
  };

  const handleClosePayment = () => {
    setQrVisible(false);
    setPaymentUrl('');
    setCurrentOrder(null);

    // 停止轮询
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  const columns = [
    {
      title: '订单号',
      dataIndex: 'order_no',
      key: 'order_no',
      width: 200
    },
    {
      title: '订单金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => <span className="order-amount">¥{amount}</span>,
      width: 120
    },
    {
      title: '订单状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusInfo = getStatusTag(status);
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
      width: 120
    },
    {
      title: '下单时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time) => new Date(time).toLocaleString('zh-CN'),
      width: 180
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {record.status === 'pending' && (
            <Button
              type="primary"
              onClick={() => handlePayment(record)}
            >
              立即支付
            </Button>
          )}
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/orders/${record.id}`)}
          >
            查看详情
          </Button>
        </div>
      ),
      width: 180
    }
  ];

  return (
    <div className="orders-container">
      <h1 className="page-title">我的订单</h1>

      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>加载中...</div>
        ) : orders.length === 0 ? (
          <Empty description="还没有订单">
            <Button type="primary" onClick={() => navigate('/mall')}>
              去购物
            </Button>
          </Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={orders}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              onChange: (page) => setPagination(prev => ({ ...prev, current: page }))
            }}
          />
        )}
      </Card>

      {/* 支付二维码弹窗 */}
      <Modal
        title="扫码支付"
        open={qrVisible}
        onCancel={handleClosePayment}
        footer={null}
        centered
        width={400}
      >
        <div className="payment-modal">
          <div className="qr-code-container" style={{ textAlign: 'center' }}>
            {paymentLoading ? (
              <div style={{ padding: '40px 0' }}>生成支付链接中...</div>
            ) : paymentUrl ? (
              <>
                <QRCode value={paymentUrl} size={240} />
                <p style={{ marginTop: 16, textAlign: 'center', color: '#1890ff' }}>
                  正在等待支付...
                </p>
              </>
            ) : (
              <p>未获取到支付链接，请稍后再试。</p>
            )}
          </div>
          {currentOrder && (
            <div className="payment-info" style={{ marginTop: 20 }}>
              <h3>订单号：{currentOrder.order_no}</h3>
              <div className="payment-price">
                <span className="price-label">支付金额：</span>
                <span className="price-value" style={{ fontSize: '24px', color: '#ff4d4f', fontWeight: 'bold' }}>
                  ¥{currentOrder.total_amount}
                </span>
              </div>
              <p className="payment-tip" style={{ color: '#666', marginTop: 10 }}>请使用微信或支付宝扫码支付</p>
              <p className="payment-tip" style={{ color: '#666' }}>支付成功后将自动更新订单状态</p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default Orders;
